// src/components/AnimatedTabPanel.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";

export default function AnimatedTabPanel({
  activeKey,
  children,
  className = "",
  exitMs = 260,
}) {
  const idRef = useRef(0);
  const [panes, setPanes] = useState(() => [
    { key: activeKey, node: children ?? null, state: "active", id: idRef.current++ },
  ]);

  const transitionStyle = useMemo(
    () => ({
      transitionDuration: "var(--motion-tab-duration)",
      transitionTimingFunction: "var(--motion-tab-ease)",
    }),
    []
  );

  useEffect(() => {
    setPanes((prev) => {
      const hasActive = prev.some(
        (pane) => pane.key === activeKey && pane.state !== "exit"
      );
      if (hasActive) {
        return prev.map((pane) =>
          pane.key === activeKey ? { ...pane, node: children ?? null } : pane
        );
      }
      return [
        ...prev.map((pane) => ({ ...pane, state: "exit" })),
        { key: activeKey, node: children ?? null, state: "enter", id: idRef.current++ },
      ];
    });
  }, [activeKey, children]);

  useEffect(() => {
    if (!panes.some((pane) => pane.state === "enter")) return;
    const raf = requestAnimationFrame(() => {
      setPanes((prev) =>
        prev.map((pane) =>
          pane.state === "enter" ? { ...pane, state: "active" } : pane
        )
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [panes]);

  useEffect(() => {
    if (!panes.some((pane) => pane.state === "exit")) return;
    const timeout = setTimeout(() => {
      setPanes((prev) => prev.filter((pane) => pane.state !== "exit"));
    }, exitMs);
    return () => clearTimeout(timeout);
  }, [panes, exitMs]);

  if (!panes.length) return null;

  const wrapperClass = ["relative", className].filter(Boolean).join(" ");

  return (
    <div className={wrapperClass}>
      {panes.map((pane) => {
        const isExiting = pane.state === "exit";
        const isEntering = pane.state === "enter";
        const base =
          "transition-[opacity,transform] will-change-transform";
        const opacityClass = isExiting
          ? "opacity-0 -translate-y-2 pointer-events-none"
          : isEntering
          ? "opacity-0 translate-y-3"
          : "opacity-100 translate-y-0";
        const positioning = isExiting ? "absolute inset-0 w-full" : "relative";
        return (
          <div
            key={pane.id}
            className={`${base} ${opacityClass} ${positioning}`}
            style={transitionStyle}
          >
            {pane.node}
          </div>
        );
      })}
    </div>
  );
}
